import {
  PAIR_AMOUNT_DECIMALS,
  USD_DECIMALS,
} from '@ui/constants/numbersDecimals';
import { getPayablePoolTokenByAddress } from '@ui/constants/tokens';
import {
  calculateSpreadPercentage,
  getSpreadPercentageInfo,
} from '@ui/features/Spread/useSpreadPercentage';
import { toReadableValue, ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { PairsV4 } from '@ui/services/contract/hooks/PairsManagerFacet/usePairsV4';
import { ContractPairMaxTpRatios } from '@ui/services/contract/hooks/TradingConfigFacet/useGetPairMaxTpRatios/query';
import { TradingConfig } from '@ui/services/contract/hooks/TradingConfigFacet/useGetTradingConfig';
import { ChainId } from '@ui/types/chain';
import { BareTradePair, TradePair } from '@ui/types/tradePair';
import { getEstPnl } from '@ui/utils/trade/getEstPnl';
import { getLeverage } from '@ui/utils/trade/getLeverage';
import { getLiqPrice } from '@ui/utils/trade/getLiqPrice';
import { getMaxTpRatios } from '@ui/utils/trade/getMaxTpRatios';
import { getOpenPositionFee } from '@ui/utils/trade/getOpenPositionFee';
import { getTakeProfitStopLossPercentage } from '@ui/utils/trade/getTakeProfitStopLossPercentage';
import { getTakeProfitStopLossPrice } from '@ui/utils/trade/getTakeProfitStopLossPrice';
import BigNumber from 'bignumber.js';

import { ContractOrder, Order } from './types';

export const toOrder = (
  order: ContractOrder,
  chainId: ChainId,
  pair: BareTradePair,
): Order => {
  const collateralToken = getPayablePoolTokenByAddress(order.tokenIn, chainId);

  if (!collateralToken) {
    throw new Error('Pay token missing in token list: ' + order.tokenIn);
  }

  const isLVTokenPay = order.lvToken.toLowerCase() === order.tokenIn.toLowerCase();
  const size = toReadableValue(order.qty, PAIR_AMOUNT_DECIMALS);
  const triggerPrice = toReadableValue(order.limitPrice, USD_DECIMALS);
  // TODO: implement the spread impacted triggerPrice once UI ready
  const amountIn = toReadableValue(order.amountIn, collateralToken.decimals);
  const stopLoss = toReadableValue(order.stopLoss, USD_DECIMALS);
  const takeProfit = toReadableValue(order.takeProfit, USD_DECIMALS);
  const sizeUsd = size.times(triggerPrice);

  const timestamp = order.timestamp;

  const _getLeverage: Order['getLeverage'] = ({
    collateralMarketPrice,
    executionFee,
    openFeeP,
    lvTokenDiscountP,
  }) => {
    return getLeverage({
      collateralUsd: _getCollateralUsd({
        collateralMarketPrice,
        lvTokenDiscountP,
        executionFee,
        openFeeP,
      }),
      sizeUsd,
    });
  };
  function getSizeUsd(pairMarketPrice = triggerPrice): BigNumber {
    // pair amount * pairMarketPrice = positionSize
    return size.times(pairMarketPrice);
  }
  const _getCollateralUsdBeforeOpenPositionFees: Order['getCollateralUsdBeforeOpenPositionFees'] =
    (collateralTokenMarketPrice: BigNumber) => {
      return amountIn.times(collateralTokenMarketPrice);
    };
  const _getCollateralUsd: Order['getCollateralUsd'] = ({
    collateralMarketPrice,
    executionFee,
    openFeeP,
    lvTokenDiscountP,
  }) => {
    return amountIn
      .times(collateralMarketPrice)
      .minus(executionFee)
      .minus(
        getOpenPositionFee({
          openFeeP,
          isPayLVToken: isLVTokenPay,
          lvTokenDiscountP: lvTokenDiscountP,
          positionSize: sizeUsd,
        }).final,
      );
  };
  function _getTpSlPercentage({
    collateralTokenMarketPrice,
    targetPrice,
    executionFee,
    openFeeP,
    lvTokenDiscountP,
    tradePair,
  }: {
    collateralTokenMarketPrice: BigNumber;
    targetPrice: number;
    openFeeP: PairsV4['feeConfig']['openFeeP'];
    lvTokenDiscountP: PairsV4['feeConfig']['lvTokenDiscountP'];
    executionFee: TradingConfig['executionFeeUsd'];
    tradePair: TradePair;
  }) {
    const spreadPercentageInfo = getSpreadPercentageInfo({
      tradePair,
      positionSize: sizeUsd,
      pairPrice: triggerPrice, // for limit order use triggerPrice to calculate spread
    });

    return getTakeProfitStopLossPercentage({
      targetPrice: targetPrice,
      entryPrice: triggerPrice.toNumber(),
      leverage: _getLeverage({
        collateralMarketPrice: collateralTokenMarketPrice,
        executionFee,
        lvTokenDiscountP,
        openFeeP,
      }).toNumber(),
      isLong: order.isLong,
      spreadPercentage: calculateSpreadPercentage({
        isLong: order.isLong,
        isClosePrice: true,
        spreadPercentageInfo,
      }),
    });
  }

  function _getTakeProfitPercentage({
    collateralTokenMarketPrice,
    executionFee,
    openFeeP,
    lvTokenDiscountP,
    tradePair,
  }: {
    collateralTokenMarketPrice: BigNumber;
    openFeeP: PairsV4['feeConfig']['openFeeP'];
    lvTokenDiscountP: PairsV4['feeConfig']['lvTokenDiscountP'];
    executionFee: TradingConfig['executionFeeUsd'];
    tradePair: TradePair;
  }) {
    return _getTpSlPercentage({
      collateralTokenMarketPrice,
      targetPrice: takeProfit.toNumber(),
      executionFee,
      openFeeP,
      lvTokenDiscountP,
      tradePair,
    });
  }

  function _getStopLossPercentage({
    collateralTokenMarketPrice,
    executionFee,
    openFeeP,
    lvTokenDiscountP,
    tradePair,
  }: {
    collateralTokenMarketPrice: BigNumber;
    pairMarketPrice: BigNumber;
    openFeeP: PairsV4['feeConfig']['openFeeP'];
    lvTokenDiscountP: PairsV4['feeConfig']['lvTokenDiscountP'];
    executionFee: TradingConfig['executionFeeUsd'];
    tradePair: TradePair;
  }) {
    return _getTpSlPercentage({
      collateralTokenMarketPrice,
      targetPrice: stopLoss.toNumber(),
      executionFee,
      openFeeP,
      lvTokenDiscountP,
      tradePair,
    });
  }

  function _getTakeProfitDeltaPnl({
    collateralTokenMarketPrice,
    executionFee,
    openFeeP,
    lvTokenDiscountP,
    tradePair,
  }: {
    collateralTokenMarketPrice: BigNumber;
    openFeeP: PairsV4['feeConfig']['openFeeP'];
    lvTokenDiscountP: PairsV4['feeConfig']['lvTokenDiscountP'];
    executionFee: TradingConfig['executionFeeUsd'];
    tradePair: TradePair;
  }) {
    const spreadPercentageInfo = getSpreadPercentageInfo({
      tradePair,
      positionSize: sizeUsd,
      pairPrice: triggerPrice, // for limit order use triggerPrice to calculate spread
    });

    return getEstPnl({
      collateralUsd: _getCollateralUsd({
        collateralMarketPrice: collateralTokenMarketPrice,
        executionFee,
        lvTokenDiscountP,
        openFeeP,
      }),
      targetPercentage: _getTakeProfitPercentage({
        collateralTokenMarketPrice,
        executionFee,
        lvTokenDiscountP,
        openFeeP,
        tradePair,
      }),
      isLong: order.isLong,
      spreadPercentage: calculateSpreadPercentage({
        isLong: order.isLong,
        isClosePrice: true,
        spreadPercentageInfo,
      }),
    });
  }

  function _getStopLossDeltaPnl({
    collateralTokenMarketPrice,
    pairMarketPrice,
    executionFee,
    openFeeP,
    lvTokenDiscountP,
    tradePair,
  }: {
    collateralTokenMarketPrice: BigNumber;
    pairMarketPrice: BigNumber;
    openFeeP: PairsV4['feeConfig']['openFeeP'];
    lvTokenDiscountP: PairsV4['feeConfig']['lvTokenDiscountP'];
    executionFee: TradingConfig['executionFeeUsd'];
    tradePair: TradePair;
  }) {
    const spreadPercentageInfo = getSpreadPercentageInfo({
      tradePair,
      positionSize: sizeUsd,
      pairPrice: triggerPrice, // for limit order use triggerPrice to calculate spread
    });

    return getEstPnl({
      collateralUsd: _getCollateralUsd({
        collateralMarketPrice: collateralTokenMarketPrice,
        executionFee,
        lvTokenDiscountP,
        openFeeP,
      }),
      targetPercentage: _getStopLossPercentage({
        collateralTokenMarketPrice,
        pairMarketPrice,
        executionFee,
        lvTokenDiscountP,
        openFeeP,
        tradePair,
      }),
      isLong: order.isLong,
      spreadPercentage: calculateSpreadPercentage({
        isLong: order.isLong,
        isClosePrice: true,
        spreadPercentageInfo,
      }),
    });
  }

  function _getMaxTpRatios({
    collateralTokenMarketPrice,
    maxTakeProfitP,
    currentPairMaxTpRatios,
    executionFee,
    openFeeP,
    lvTokenDiscountP,
  }: {
    collateralTokenMarketPrice: BigNumber;
    currentPairMaxTpRatios: ContractPairMaxTpRatios[];
    maxTakeProfitP: number;
    openFeeP: PairsV4['feeConfig']['openFeeP'];
    lvTokenDiscountP: PairsV4['feeConfig']['lvTokenDiscountP'];
    executionFee: TradingConfig['executionFeeUsd'];
  }) {
    return getMaxTpRatios({
      leverage: _getLeverage({
        collateralMarketPrice: collateralTokenMarketPrice,
        executionFee,
        openFeeP,
        lvTokenDiscountP,
      }).toNumber(),
      maxTakeProfitP: maxTakeProfitP,
      currentPairMaxTpRatios: currentPairMaxTpRatios,
    });
  }

  function _getMaxTpRatiosPrice({
    collateralTokenMarketPrice,
    maxTakeProfitP,
    currentPairMaxTpRatios,
    executionFee,
    openFeeP,
    lvTokenDiscountP,
    tradePair,
  }: {
    collateralTokenMarketPrice: BigNumber;
    currentPairMaxTpRatios: ContractPairMaxTpRatios[];
    maxTakeProfitP: number;
    openFeeP: PairsV4['feeConfig']['openFeeP'];
    lvTokenDiscountP: PairsV4['feeConfig']['lvTokenDiscountP'];
    executionFee: TradingConfig['executionFeeUsd'];
    tradePair: TradePair;
  }) {
    const spreadPercentageInfo = getSpreadPercentageInfo({
      tradePair,
      positionSize: sizeUsd,
      pairPrice: triggerPrice, // for limit order use triggerPrice to calculate spread
    });

    return getTakeProfitStopLossPrice({
      pnlRate: _getMaxTpRatios({
        collateralTokenMarketPrice,
        maxTakeProfitP,
        currentPairMaxTpRatios,
        executionFee,
        openFeeP,
        lvTokenDiscountP,
      }),
      isTakeProfit: true,
      entryPrice: triggerPrice.toNumber(),
      leverage: _getLeverage({
        collateralMarketPrice: collateralTokenMarketPrice,
        executionFee,
        openFeeP,
        lvTokenDiscountP,
      }).toNumber(),
      isLong: order.isLong,
      spreadPercentage: calculateSpreadPercentage({
        isLong: order.isLong,
        isClosePrice: true,
        spreadPercentageInfo,
      }),
    });
  }

  return {
    id: order.orderHash,
    isLong: order.isLong,
    pair,
    size,
    sizeUsd,
    triggerPrice,
    amountIn,
    collateralToken,
    stopLoss,
    takeProfit,
    timestamp,
    getSizeUsd: getSizeUsd,
    getCollateralUsd: _getCollateralUsd,
    getCollateralUsdBeforeOpenPositionFees(collateralTokenMarketPrice) {
      return _getCollateralUsdBeforeOpenPositionFees(
        collateralTokenMarketPrice,
      );
    },
    getLeverage: _getLeverage,
    getMaxTpRatios: _getMaxTpRatios,
    getMaxTpRatiosPrice({
      collateralTokenMarketPrice,
      maxTakeProfitP,
      currentPairMaxTpRatios,
      executionFee,
      openFeeP,
      lvTokenDiscountP,
      tradePair,
    }) {
      return _getMaxTpRatiosPrice({
        collateralTokenMarketPrice,
        maxTakeProfitP,
        currentPairMaxTpRatios,
        executionFee,
        openFeeP,
        lvTokenDiscountP,
        tradePair,
      });
    },
    getTakeProfitPercentage({
      collateralTokenMarketPrice,
      executionFee,
      openFeeP,
      lvTokenDiscountP,
      tradePair,
    }) {
      return _getTakeProfitPercentage({
        collateralTokenMarketPrice,
        executionFee,
        openFeeP,
        lvTokenDiscountP,
        tradePair,
      });
    },
    getTakeProfitDeltaPnl({
      collateralTokenMarketPrice,
      executionFee,
      openFeeP,
      lvTokenDiscountP,
      tradePair,
    }) {
      return _getTakeProfitDeltaPnl({
        collateralTokenMarketPrice,
        executionFee,
        openFeeP,
        lvTokenDiscountP,
        tradePair,
      });
    },
    getStopLossPercentage({
      collateralTokenMarketPrice,
      pairMarketPrice,
      executionFee,
      openFeeP,
      lvTokenDiscountP,
      tradePair,
    }) {
      return _getStopLossPercentage({
        collateralTokenMarketPrice,
        pairMarketPrice,
        executionFee,
        openFeeP,
        lvTokenDiscountP,
        tradePair,
      });
    },
    getStopLossDeltaPnl({
      collateralTokenMarketPrice,
      pairMarketPrice,
      executionFee,
      openFeeP,
      lvTokenDiscountP,
      tradePair,
    }) {
      return _getStopLossDeltaPnl({
        collateralTokenMarketPrice,
        pairMarketPrice,
        executionFee,
        openFeeP,
        lvTokenDiscountP,
        tradePair,
      });
    },
    getLiquidationPrice({
      collateralMarketPrice,
      liqLostP,
      executionFee,
      openFeeP,
      lvTokenDiscountP,
      tradePair,
    }) {
      const spreadPercentageInfo = getSpreadPercentageInfo({
        tradePair,
        positionSize: size.times(triggerPrice),
        pairPrice: triggerPrice,
      });

      const slippageP = calculateSpreadPercentage({
        isLong: order.isLong,
        isClosePrice: true,
        spreadPercentageInfo,
      });

      return getLiqPrice({
        positionQty: size,
        entryPrice: triggerPrice,
        isLong: order.isLong,
        liqLostP,
        slippageP,
        countableCollateralUsd: _getCollateralUsd({
          collateralMarketPrice,
          executionFee,
          openFeeP,
          lvTokenDiscountP,
        }),
        // for open position flow, the countableFeesUsd is 0 due to do not have funding fee and holding fee; for existing position, it will need to included funding fee + holding fee + close fee
        closeFee: {
          closeFee: ZERO_BIG_NUMBER,
          finalCloseFee: ZERO_BIG_NUMBER,
        },
        fundingFee: ZERO_BIG_NUMBER,
        holdingFee: ZERO_BIG_NUMBER,
      });
    },
  };
};
